import { format, parseISO } from "date-fns";
import { Check, Trash2 } from "lucide-react";

function ShoppingListItem({ item, toggleItem, deleteItem, isLast }) {
  return (
    <div
      className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-gray-50 transition-colors group ${
        isLast ? "rounded-b-lg" : ""
      }`}
    >
      <button
        onClick={() => toggleItem(item.itemId)}
        className={`w-6 h-6 sm:w-5 sm:h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          item.checked
            ? "bg-green-500 border-green-500"
            : "border-gray-300 hover:border-gray-400"
        }`}
      >
        {item.checked && (
          <Check className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium ${
            item.checked ? "line-through text-gray-400" : "text-gray-900"
          }`}
        >
          {item.itemName}
        </p>
        <p className="text-xs text-gray-500">
          {item.itemQuantity} {item.itemUnit}
        </p>
      </div>

      <button
        onClick={() => deleteItem(item.itemId)}
        className="opacity-100 sm:opacity-50 sm:group-hover:opacity-100 p-2 hover:bg-gray-100 rounded transition-opacity flex-shrink-0"
      >
        <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500 hover:scale-110 transition-transform" />
      </button>
    </div>
  );
}

function SectionHeader({ label, dateRange, isFirst }) {
  return (
    <div
      className={`flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200 ${
        isFirst ? "rounded-t-lg" : "border-t border-gray-200"
      }`}
    >
      <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
        {label}
      </span>
      {dateRange && (
        <span className="text-xs text-gray-500">{dateRange}</span>
      )}
    </div>
  );
}

function getSectionLabel(index) {
  if (index === 0) return "This week";
  if (index === 1) return "Next week";
  return `In ${index} weeks`;
}

export function ShoppingListSection({
  items,
  toggleItem,
  deleteItem,
  shoppingWindow,
  weekStartDates,
}) {
  const showSections = shoppingWindow !== "THIS_WEEK" && weekStartDates?.length > 1;

  if (!showSections) {
    return (
      <div className="max-w-3xl">
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200 overflow-hidden">
          {items.map((item, i) => (
            <ShoppingListItem
              key={item.itemId}
              item={item}
              toggleItem={toggleItem}
              deleteItem={deleteItem}
              isLast={i === items.length - 1}
            />
          ))}
        </div>
      </div>
    );
  }

  const firstWeek = weekStartDates[0];
  const groups = weekStartDates.map((date) => ({
    date,
    items: items.filter((item) =>
      item.weekStartDate === date || (item.weekStartDate === null && date === firstWeek)
    ),
  }));

  // Flatten to a single list of rows (headers + items) so we can track the true last item
  const rows = [];
  groups.forEach((group, groupIndex) => {
    rows.push({ type: "header", group, groupIndex });
    group.items.forEach((item, itemIndex) => {
      rows.push({ type: "item", item, itemIndex, groupItems: group.items });
    });
    if (group.items.length === 0) {
      rows.push({ type: "empty", group });
    }
  });

  const lastItemRowIndex = [...rows].reverse().findIndex((r) => r.type === "item");
  const lastItemIndex = lastItemRowIndex === -1 ? -1 : rows.length - 1 - lastItemRowIndex;

  return (
    <div className="max-w-3xl">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {rows.map((row, i) => {
          if (row.type === "header") {
            return (
              <SectionHeader
                key={`header-${row.group.date}`}
                label={getSectionLabel(row.groupIndex)}
                dateRange={format(parseISO(row.group.date), "MMM d")}
                isFirst={i === 0}
              />
            );
          }
          if (row.type === "item") {
            return (
              <div key={row.item.itemId} className="border-t border-gray-200">
                <ShoppingListItem
                  item={row.item}
                  toggleItem={toggleItem}
                  deleteItem={deleteItem}
                  isLast={i === lastItemIndex}
                />
              </div>
            );
          }
          return (
            <p key={`empty-${row.group.date}`} className="text-sm text-gray-400 italic px-4 py-3 border-t border-gray-200">
              No meals planned for this week yet.
            </p>
          );
        })}
      </div>
    </div>
  );
}
